// Libraries
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

// API
import { updateGpsLots, deleteGpsLot } from "@/Settings/api/AdministrationTools/mutations";

// Components
import LoadLotsButton from "./LoadLotsButton";
import LotArea from "./LotArea";

// Types
import { ILot } from "@/Settings/types/administrationTools/model";
import { GPSSelectedProvider } from "@/Settings/types/administrationTools/utils";

// Helpers
import { or } from "@/Common/helpers/sonar";
import { hasSomeRoleLoggedIn } from "@/Settings/helpers/AdministrationTools/Provider/utils";

// Constants
import { PROVIDERS_WITH_STORE_FEATURE } from "@/Settings/Constants/AdministrationTools/AdministrationTools";

// Hooks
import useLotsSubscription from "@/Settings/hooks/useLotsSubscription";
import { updateGpsLotsCache } from "@/Settings/helpers/AdministrationTools/Provider/globalPositioningService";

type TLotInformationProps = {
	locationId: string;
	selectedProvider: GPSSelectedProvider | null;
	lots: ILot[];
};
export default function LotInformation(props: Readonly<TLotInformationProps>) {
	const { locationId, lots = [] } = props;
	const { providerName, providerDBProperty = "", gpsStoreId = "" } = props.selectedProvider ?? {};

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [deletingLotId, setDeletingLotId] = useState<string | null>(null);

	const { mutate: refreshLots } = useMutation({
		mutationKey: ["refreshLots", locationId],
		onMutate: () => setIsLoading(true),
		mutationFn: async () => updateGpsLots(locationId, providerDBProperty, gpsStoreId),
	});
	const queryClient = useQueryClient();
	const { mutate: removeGpsLot } = useMutation({
		mutationKey: ["deleteGpsLot", locationId],
		mutationFn: async ({ location, lotId }: { location: string; lotId: string }) => {
			return deleteGpsLot(location, lotId);
		},
		onMutate: ({ lotId }) => {
			setDeletingLotId(lotId);
		},
		onSuccess: (data, variables) => {
			if (!data) {
				return;
			}
			updateGpsLotsCache(locationId, queryClient, variables);
		},
		onSettled: () => {
			setDeletingLotId(null);
		},
	});

	const isDisabled = or(
		isLoading,
		!providerName,
		!gpsStoreId,
		!PROVIDERS_WITH_STORE_FEATURE.includes(providerDBProperty),
		!hasSomeRoleLoggedIn(props.selectedProvider)
	);

	useLotsSubscription({ locationId, subscriptionKey: "stopLoading", callback: () => setIsLoading(false) });

	const handleRemoveLot = (lotId: string) => {
		removeGpsLot({ location: locationId, lotId });
	};
	return (
		<Stack>
			<Typography className="!font-montserrat !font-medium !text-[16px] text-default !mt-8">Lot Information</Typography>
			<LoadLotsButton disabled={isDisabled} refresh={refreshLots} />
			<LotArea lots={lots ?? []} isLoading={isLoading} removeLot={handleRemoveLot} deletingLotId={deletingLotId} />
		</Stack>
	);
}
